import React from 'react';
import { Card, CardContent, Box, Paper, Typography } from '@mui/material';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';

interface PercentageItem {
    topic: string;
    percentage: number;
    colorFrom?: string;
    colorTo?: string;
    info?: string;
}

interface PercentageLineChartProps extends Partial<ChartHeaderProps> {
    data: PercentageItem[];
    rowHeight?: number;
    height?: number;
    onDetailsClick?: (data?: any) => void;
}

const DEFAULT_FROM = '#FF9A66';
const DEFAULT_TO = '#FF59C3';

const PercentageLineChart: React.FC<PercentageLineChartProps> = ({
    title = 'Progress',
    description,
    details,
    data,
    rowHeight = 18,
    height = 400,
    onDetailsClick,
}) => {
    const handleClick = (data: any) => {
        if (onDetailsClick) {
            onDetailsClick(data);
        }
    };
    return (
        <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
                <ChartHeader title={title} description={description || ''} details={details} />

                {details?.label && (
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'left', mb: 1 }}>
                        {details.label}
                    </Typography>
                )}

                {details?.subLabel && (
                    <Typography variant="caption" color="textSecondary" gutterBottom sx={{ textAlign: 'left', mb: 2, display: 'block' }}>
                        {details.subLabel}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, }}>
                    {data.map((item, idx) => {
                        const from = item.colorFrom || DEFAULT_FROM;
                        const to = item.colorTo || DEFAULT_TO;
                        const pct = Math.max(0, Math.min(100, Math.round(item.percentage)));

                        return (
                            <Box key={idx} sx={{ cursor: 'pointer' }} onClick={() => handleClick(item)}>
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ minWidth: 80 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.topic}</Typography>
                                    </Box>

                                    <Box sx={{ position: 'relative', flex: 1, height: rowHeight, display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{
                                            position: 'absolute',
                                            left: 0,
                                            right: 0,
                                            height: rowHeight,
                                            borderRadius: rowHeight / 2,
                                            background: 'linear-gradient(90deg, rgba(15,23,42,0.04), rgba(15,23,42,0.02))'
                                        }} />

                                        <Box sx={{
                                            position: 'relative',
                                            height: rowHeight,
                                            borderRadius: rowHeight / 2,
                                            width: `${pct}%`,
                                            background: `linear-gradient(90deg, ${from}, ${to})`,
                                            transition: 'width 600ms ease'
                                        }} />

                                        <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                                            <Paper elevation={3} sx={{ px: 1.2, py: 0.4, borderRadius: 2, minWidth: 44, textAlign: 'center' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{pct}%</Typography>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </Box>
                                {item.info && (
                                    <Box sx={{ textAlign: 'left', marginLeft: '10px', marginTop: '4px' }}>
                                        <Typography variant="body2" color="textSecondary">{item.info}</Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {details?.bottomDescription && (
                    <Typography variant="caption" color="textSecondary" gutterBottom sx={{ textAlign: 'left', mt: 2, display: 'block' }}>
                        {details.bottomDescription}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default PercentageLineChart;
